import { lazy, type ComponentType } from 'react';

const KEY = 'lazy-reload-at';

/**
 * React.lazy that survives a deploy: when a chunk fails to load (its hash
 * changed under an open tab, or the network blipped), reload the page once
 * to pick up the new build instead of throwing into the error boundary.
 * A second failure within a minute throws as before, so it never loops.
 */
export function lazyWithReload<T extends ComponentType<never>>(factory: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      let last = 0;
      try {
        last = Number(sessionStorage.getItem(KEY) ?? 0);
      } catch {
        // Storage blocked: fall through and throw
      }
      if (Date.now() - last > 60_000) {
        try {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          // Keep Suspense showing its fallback while the page reloads
          return await new Promise<never>(() => {});
        } catch {
          // Could not record the attempt: throw rather than risk a loop
        }
      }
      throw error;
    }
  });
}
